import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";

import { keys } from "lodash";

import brokeragesTableConfig from "@/core/config/BrokeragesTableConfig";
import usersTableConfig from "@/core/config/UsersTableConfig";
import { useUserStore } from "@/stores/UserStore";
import insuranceTypesTableConfig from "@/core/config/InsuranceTypesTableConfig";
import rolesTableConfig from "@/core/config/RolesTableConfig";
import Applications from "@/helpers/clients/Applications";
import { quoteCardConfig } from "@/resources/quote.card";
import { quoteTableConfig } from "@/resources/quote.table";
import ApplicationsError from "@/core/errors/ApplicationsError";
import { ResourceType } from "@/enums/resourceType.enum";
import { policyCardConfig } from "@/resources/policy.card";
import { policyTableConfig } from "@/resources/policy.table";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    redirect: "/iniciar-sesion",
    component: () => import("@/layouts/MasterLayout.vue"),
    children: [
      {
        path: "",
        name: "Index",
        component: () => import("@/views/Index.vue"),
        meta: {
          requiresAuth: true,
          viewSettings: {
            header: {
              show: true,
            },
          },
        },
      },
      {
        path: "polizas",
        name: "Polizas",
        component: () => import("@/views/BaseRecordsView.vue"),
        props: (route) => ({ resource: route.meta.resource }),
        meta: {
          resource: {
            key: "policies",
            client: () => new Applications(),
            display: { card: policyCardConfig, table: policyTableConfig },
            titles: { singular: "Póliza", plural: "Pólizas" },
            errorFactory: (error: any) => new ApplicationsError(error, "póliza"),
            extraQuery: { application_type_id: process.env.VUE_APP_POLICY_APPLICATION_TYPE_TAG },
            sortParam: "sort_by",
            defaultSort: "created_at desc",
            sortOptions: [
              { label: "Más recientes", value: "created_at desc" },
              { label: "Más antiguas", value: "created_at asc" },
            ],
            searchBar: () => import("@/core/config/PoliciesSearchBarConfig").then((m) => m.getFiltersConfig()),
          },
          viewSettings: {
            header: {
              show: true,
            },
          },
          requiredPermissions: [["applications-management.applications.search"]],
          requiresAuth: true,
        },
      },
      {
        path: "productos",
        name: "Productos",
        component: () => import("@/views/Processes.vue"),
        meta: {
          inboxType: "processes",
          viewSettings: {
            header: {
              title: "Seleccione su póliza",
              subtitle: "Elija el tipo de seguro que necesita para la solicitud",
              show: true,
            },
            table: {
              actionPermissions: [
                ["applications-management.applications.create"]
              ],
              config: insuranceTypesTableConfig,
            },
            card: {
              type: "InsuranceTypeCard",
            },
          },
          pluralTitle: "Productos",
          singularTitle: "Producto",
          requiredPermissions: [["processes-management.processes.read"], ["processes-management.processes.search"]],
          requiresAuth: true,
        },
      },
      {
        path: "cotizaciones",
        name: "Cotizaciones",
        component: () => import("@/views/BaseRecordsView.vue"),
        props: (route) => ({ resource: route.meta.resource }),
        meta: {
          inboxType: "quotes",
          resource: {
            key: "quotes",
            client: () => new Applications(),
            display: { card: quoteCardConfig, table: quoteTableConfig },
            titles: { singular: "Cotización", plural: "Cotizaciones" },
            errorFactory: (error: any) => new ApplicationsError(error, "cotización"),
            optimistic: ResourceType.QUOTE,
            extraQuery: { application_type_id: process.env.VUE_APP_QUOTE_APPLICATION_TYPE_TAG },
            sortParam: "sort_by",
            defaultSort: "created_at desc",
            sortOptions: [
              { label: "Más recientes", value: "created_at desc" },
              { label: "Más antiguas", value: "created_at asc" },
            ],
            searchBar: () => import("@/core/config/QuotesSearchBarConfig").then((m) => m.getFiltersConfig()),
          },
          viewSettings: {
            header: {
              show: true,
            },
          },
          pluralTitle: "Cotizaciones",
          singularTitle: "Cotización",
          requiredPermissions: [["applications-management.applications.search"]],
          requiresAuth: true,
        },
      },
      {
        path: "/cotizaciones/:processId/crear",
        name: "CreateApplication",
        component: () => import("@/views/ApplicationCreation.vue"),
        meta: {
          mode: "create",
          breadcrumbs: [
            {
              icon: "home",
              tooltipText: "Cotizaciones",
              to: {
                name: "Cotizaciones",
              },
            },
            {
              text: "Crear cotización",
            },
          ],
          requiredPermissions: [
            ["processes-management.processes.read"],
            ["applications-management.applications.create"],
            ["applications-management.application-steps.update"],
          ],
          requiresAuth: true,
        },
      },
      {
        path: "/cotizaciones/:id/pasos/actual/modificar",
        name: "EditCurrentStep",
        component: () => import("@/views/QuoteRenderer.vue"),
        meta: {
          applicationTypeId: process.env.VUE_APP_QUOTE_APPLICATION_TYPE_TAG,
          mode: "modification",
          breadcrumbs: [
            {
              icon: "home",
              tooltipText: "Cotizaciones",
              to: {
                name: "Cotizaciones",
              },
            },
            "addStepName",
          ],
          requiredPermissions: [
            ["applications-management.applications.read"],
            ["applications-management.applications.update"],
            ["applications-management.application-steps.search"],
            ["applications-management.application-steps.read"],
            ["applications-management.application-steps.update"],
          ],
          requiresAuth: true,
        },
      },
      {
        path: "/cotizaciones/:id/pasos/:stepId/modificar",
        name: "EditStep",
        component: () => import("@/views/QuoteRenderer.vue"),
        meta: {
          applicationTypeId: process.env.VUE_APP_QUOTE_APPLICATION_TYPE_TAG,
          mode: "modification",
          breadcrumbs: [
            {
              icon: "home",
              tooltipText: "Cotizaciones",
              to: {
                name: "Cotizaciones",
              },
            },
            "addStepName",
          ],
          requiredPermissions: [
            ["processes-management.processes.read"],
            ["applications-management.applications.read"],
            ["applications-management.application-steps.update"],
          ],
          requiresAuth: true,
        },
      },
      {
        path: "/cotizaciones/:id/pasos/actual/consultar",
        name: "ReadCurrentStep",
        component: () => import("@/views/QuoteRenderer.vue"),
        meta: {
          applicationTypeId: process.env.VUE_APP_QUOTE_APPLICATION_TYPE_TAG,
          mode: "visualization",
          breadcrumbs: [
            {
              icon: "home",
              tooltipText: "Cotizaciones",
              to: {
                name: "Cotizaciones",
              },
            },
            "addStepName",
          ],
          requiredPermissions: [
            ["applications-management.applications.read"],
            ["applications-management.application-steps.search"],
            ["applications-management.application-steps.read"],
          ],
          requiresAuth: true,
        },
      },
      {
        path: "/cotizaciones/:id/pasos/:stepId/consultar",
        name: "ReadStep",
        component: () => import("@/views/QuoteRenderer.vue"),
        meta: {
          applicationTypeId: process.env.VUE_APP_QUOTE_APPLICATION_TYPE_TAG,
          mode: "visualization",
          breadcrumbs: [
            {
              icon: "home",
              tooltipText: "Cotizaciones",
              to: {
                name: "Cotizaciones",
              },
            },
            "addStepName",
          ],
          requiredPermissions: [
            ["processes-management.processes.read"],
            ["applications-management.applications.read"],
            ["applications-management.application-steps.read"],
          ],
          requiresAuth: true,
        },
      },
      {
        path: "/polizas/:id/pasos/:stepId/consultar",
        name: "ReadPolicy",
        component: () => import("@/views/PolicyRenderer.vue"),
        meta: {
          applicationTypeId: process.env.VUE_APP_POLICY_APPLICATION_TYPE_TAG,
          singularTitle: "Póliza",
          mode: "visualization",
          breadcrumbs: [
            {
              icon: "home",
              tooltipText: "Pólizas",
              to: {
                name: "Polizas",
              },
            },
            "addStepName",
          ],
          requiredPermissions: [
            ["processes-management.processes.read"],
            ["applications-management.applications.read"],
            ["applications-management.application-steps.read"],
          ],
          requiresAuth: true,
        },
      },
      {
        path: "/corredurias",
        name: "Brokerages",
        component: () => import("@/views/Brokerages.vue"),
        meta: {
          singularTitle: "Correduría",
          pluralTitle: "Corredurías",
          viewSettings: {
            header: {
              show: true,
            },
            searchBar: {
              config: () => import("@/core/config/BrokerageSearchBarConfig"),
            },
            table: {
              actionPermissions: [["categories-management.tags.read", "categories-management.tags.update"]],
              config: brokeragesTableConfig,
            },
            card: {
              type: "BrokerageCard",
            },
          },
          requiredPermissions: [["categories-management.tags.create"], ["categories-management.tags.update"], ["categories-management.tags.delete"]],
          requiresAuth: true,
        },
      },
      {
        path: "/corredurias/crear",
        name: "CreateBrokerage",
        component: () => import("@/views/CUBrokerage.vue"),
        meta: {
          breadcrumbs: [
            {
              icon: "home",
              tooltipText: "Inicio",
              to: {
                name: "Index",
              },
            },
            {
              tooltipText: "Corredurías",
              text: "Corredurías",
              to: {
                name: "Brokerages",
              },
            },
            {
              text: "Crear correduría",
            },
          ],
          requiredPermissions: [
            ["categories-management.tags.create"],
          ],
          requiresAuth: true,
        },
      },
      {
        path: "/corredurias/:id/modificar",
        name: "UpdateBrokerage",
        component: () => import("@/views/CUBrokerage.vue"),
        meta: {
          breadcrumbs: [
            {
              icon: "home",
              tooltipText: "Inicio",
              to: {
                name: "Index",
              },
            },
            {
              tooltipText: "Corredurías",
              text: "Corredurías",
              to: {
                name: "Brokerages",
              },
            },
            {
              text: "Modificar correduría",
            },
          ],
          requiredPermissions: [
            ["categories-management.tags.update"],
          ],
          requiresAuth: true,
        },
      },
      {
        name: "Roles",
        path: "/roles",
        component: () => import("@/views/Roles.vue"),
        meta: {
          inboxType: "roles",
          requiredPermissions: [["roles-management.roles.search", "roles-management.roles.read"]],
          requiresAuth: true,
          singularTitle: "Rol",
          pluralTitle: "Roles",
          viewSettings: {
            header: {
              show: true,
            },
            table: {
              actionPermissions: [
                ["roles-management.roles.read", "roles-management.roles.update", "roles-management.roles.delete"],
              ],
              config: rolesTableConfig,
            },
            card: {
              type: "RoleCard",
            },
          },
        },
      },
      {
        path: "/roles/crear",
        name: "createRole",
        component: () => import("@/views/CURoles.vue"),
        meta: {
          breadcrumbs: [
            {
              icon: "home",
              tooltipText: "Inicio",
              to: {
                name: "Index",
              },
            },
            {
              tooltipText: "Roles",
              text: "Roles",
              to: {
                name: "Roles",
              },
            },
            {
              text: "Crear rol",
            },
          ],
          requiredPermissions: [["roles-management.roles.create"]],
          requiresAuth: true,
        },
      },
      {
        path: "/roles/:id/modificar",
        name: "updateRole",
        component: () => import("@/views/CURoles.vue"),
        meta: {
          breadcrumbs: [
            {
              icon: "home",
              tooltipText: "Inicio",
              to: {
                name: "Index",
              },
            },
            {
              tooltipText: "Roles",
              text: "Roles",
              to: {
                name: "Roles",
              },
            },
            {
              text: "Modificar rol",
            },
          ],
          requiredPermissions: [["roles-management.roles.update"]],
          requiresAuth: true,
        },
      },
      {
        path: "no-access",
        name: "NoAccess",
        component: () => import("@/views/NoAccess.vue"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: "/:pathMatch(.*)*",
        name: "PageNotFound",
        component: () => import("@/views/PageNotFound.vue"),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: "/cotizaciones/:id/pasos/finalizar/modificar",
        name: "QuotedFinished",
        component: () => import("@/views/QuoteFinished.vue"),
        meta: {
          mode: "modification",
          breadcrumbs: [
            {
              icon: "home",
              tooltipText: "Cotizaciones",
              to: {
                name: "Cotizaciones",
              },
            },
            {
              text: "Crear cotización",
            },
          ],
          requiredPermissions: [
            ["processes-management.processes.read"],
            ["applications-management.applications.read"],
            ["applications-management.application-steps.read", "applications-management.application-steps.update"],
          ],
          requiresAuth: true,
        },
      },
    ],
  },
  {
    path: "/iniciar-sesion",
    name: "Login",
    component: () => import("@/views/Login.vue"),
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach((to, from) => {
  const userStore = useUserStore();

  if (to.name === "Login" && keys(userStore.user || {}).length && userStore.isTokenValid()) {
    return { name: "Index" };
  }

  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (!userStore.isTokenValid()) {
      const cause: string = !userStore.token ? "unauthenticated" : "token-expired";
      return { name: "Login", query: { redirect: to.path, redirect_cause: cause } };
    }

    if (
      to.name !== "Index" &&
      to.name !== "NoAccess" &&
      to.name !== "PageNotFound" &&
      !userStore.can((to.meta.requiredPermissions as string[][]) || [])
    ) {
      return { name: "NoAccess" };
    }
  }
});

export default router;
